import React, { useEffect, useMemo, useState } from 'react';
import { BOAPI } from '../service/UsersService';
import ConfirmModal from './ConfirmModal';

// טאב "שליחת התראות" - שידור Push למשתמשי האפליקציה, בפילוח: כל
// המשתמשים, או קבוצה/יחיד שנבחרים ידנית מרשימה עם חיפוש (בחירה מרובה -
// אותו רכיב בדיוק משרת גם "משתמש בודד", פשוט עם בחירה אחת). רק משתמשים
// עם hasPushToken (אישרו התראות במכשיר) נספרים בפועל בשרת (spBOGetPushTargets)
// גם אם המנהל בחר גם משתמשים בלי טוקן - הם פשוט לא יקבלו כלום
const NotificationsTab = () => {
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);

  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [targetMode, setTargetMode] = useState('all'); // 'all' | 'specific'
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [search, setSearch] = useState('');
  const [categories, setCategories] = useState([]);
  const [categoryKey, setCategoryKey] = useState(''); // '' = בלי פילוח לפי תחום עניין

  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [showSendConfirm, setShowSendConfirm] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const resp = await BOAPI.fetchUsers();
        if (resp?.[0]?.[0]?.code === 0) setUsers(resp[1] || []);
      } catch {
        // שקט - אם הרשימה לא נטענה עדיין אפשר לשלוח ל"כולם", רק בחירה ידנית תיפגע
      } finally {
        setUsersLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const resp = await BOAPI.fetchBusinessCategories();
        if (resp?.[0]?.[0]?.code === 0) setCategories(resp[1] || []);
      } catch {
        // שקט - בלי הרשימה פשוט אי אפשר לפלח לפי תחום עניין
      }
    })();
  }, []);

  const notifiableUsers = useMemo(() => users.filter((u) => u.hasPushToken), [users]);

  const filteredUsers = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return notifiableUsers;
    return notifiableUsers.filter((u) =>
      `${u.fName} ${u.lName} ${u.phone}`.toLowerCase().includes(q)
    );
  }, [notifiableUsers, search]);

  const toggleUser = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selectedUsers = users.filter((u) => selectedIds.has(u.id));

  const canSend =
    title.trim() && body.trim() && !sending &&
    (targetMode === 'all' || selectedIds.size > 0);

  const targetLabel = targetMode === 'all'
    ? `כל המשתמשים (${notifiableUsers.length} עם התראות פעילות)`
    : `${selectedIds.size} משתמשים נבחרים`;

  const handleSend = () => {
    if (!canSend) return;
    setShowSendConfirm(true);
  };

  const doSend = async () => {
    setShowSendConfirm(false);
    setSending(true);
    setError('');
    setSuccessMsg('');
    try {
      const params = {
        title: title.trim(),
        body: body.trim(),
        ...(targetMode === 'specific' ? { userIds: Array.from(selectedIds) } : {}),
        ...(categoryKey ? { categoryKey } : {}),
      };
      const resp = await BOAPI.sendPushNotification(params);
      if (resp?.[0]?.[0]?.code === 0) {
        const count = resp[1]?.[0]?.sentCount ?? 0;
        setSuccessMsg(`ההתראה נשלחה ל-${count} משתמשים`);
        setTitle('');
        setBody('');
        setSelectedIds(new Set());
      } else {
        setError(resp?.[0]?.[0]?.msg || 'שליחת ההתראה נכשלה');
      }
    } catch {
      setError('שגיאה בתקשורת עם השרת');
    } finally {
      setSending(false);
    }
  };

  return (
    <div style={s.container}>
      <div style={s.headerRow}>
        <h2 style={s.title}>שליחת התראות</h2>
      </div>

      <p style={s.hint}>שולח הודעת Push למשתמשים שאישרו קבלת התראות באפליקציה. משתמש שלא אישר לא יקבל כלום, גם אם נבחר. משתמש שכיבה התראות (או הודעות כלליות) במסך ההגדרות באפליקציה לא יקבל גם הוא.</p>

      {error && (
        <div style={s.errorBanner}>❌ {error}
          <button style={s.closeErr} onClick={() => setError('')}>✕</button>
        </div>
      )}
      {successMsg && <div style={s.successBanner}>✅ {successMsg}</div>}

      <div style={s.form}>
        <div style={s.fieldGroup}>
          <label style={s.fieldLabel}>כותרת ההתראה *</label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            style={s.input}
            placeholder="לדוגמה: הטבה חדשה במועדון!"
            maxLength={100}
          />
        </div>

        <div style={s.fieldGroup}>
          <label style={s.fieldLabel}>תוכן ההתראה *</label>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            style={s.textarea}
            placeholder="תוכן ההודעה שיוצג למשתמש..."
            maxLength={400}
          />
        </div>

        <div style={s.fieldGroup}>
          <label style={s.fieldLabel}>נמענים</label>
          <div style={s.targetToggle}>
            <button
              type="button"
              style={{ ...s.targetBtn, ...(targetMode === 'all' ? s.targetBtnActive : {}) }}
              onClick={() => setTargetMode('all')}
            >
              כל המשתמשים
            </button>
            <button
              type="button"
              style={{ ...s.targetBtn, ...(targetMode === 'specific' ? s.targetBtnActive : {}) }}
              onClick={() => setTargetMode('specific')}
            >
              בחירה ידנית (יחיד/מספר משתמשים)
            </button>
          </div>

          {targetMode === 'all' && (
            <p style={s.targetSummary}>
              {usersLoading ? 'טוען...' : `ההתראה תישלח ל-${notifiableUsers.length} משתמשים עם התראות פעילות`}
            </p>
          )}

          {targetMode === 'specific' && (
            <div style={s.pickerWrap}>
              {selectedUsers.length > 0 && (
                <div style={s.chipsRow}>
                  {selectedUsers.map((u) => (
                    <span key={u.id} style={s.chip}>
                      {u.fName} {u.lName}
                      <button type="button" style={s.chipRemove} onClick={() => toggleUser(u.id)}>✕</button>
                    </span>
                  ))}
                </div>
              )}
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={s.searchInput}
                placeholder="חיפוש לפי שם או שם משתמש..."
              />
              <div style={s.userList}>
                {usersLoading ? (
                  <div style={s.emptyText}>טוען משתמשים...</div>
                ) : filteredUsers.length === 0 ? (
                  <div style={s.emptyText}>לא נמצאו משתמשים עם התראות פעילות</div>
                ) : (
                  filteredUsers.map((u) => (
                    <label key={u.id} style={s.userRow}>
                      <input type="checkbox" checked={selectedIds.has(u.id)} onChange={() => toggleUser(u.id)} />
                      <span>{u.fName} {u.lName} <span style={s.userUsername}>({u.phone})</span></span>
                    </label>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        <div style={s.fieldGroup}>
          <label style={s.fieldLabel}>תחום עניין (לא חובה)</label>
          <select value={categoryKey} onChange={(e) => setCategoryKey(e.target.value)} style={s.input}>
            <option value="">כל הקטגוריות</option>
            {categories.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
          </select>
          <p style={s.targetSummary}>
            בחרו קטגוריה כדי לשלוח רק למי שהגדיר בהעדפות ההתראות שהתחום מעניין אותו (או שלא סינן תחומים בכלל).
          </p>
        </div>

        <button type="button" style={{ ...s.sendBtn, ...(!canSend ? s.sendBtnDisabled : {}) }} onClick={handleSend} disabled={!canSend}>
          {sending ? 'שולח...' : 'שליחת ההתראה'}
        </button>
      </div>

      <ConfirmModal
        visible={showSendConfirm}
        title="שליחת התראה"
        message={`לשלוח את ההתראה ל${targetLabel}?`}
        confirmLabel="שליחה"
        onConfirm={doSend}
        onCancel={() => setShowSendConfirm(false)}
      />
    </div>
  );
};

const s = {
  container:    { padding: 20, direction: 'rtl', fontFamily: 'Arial, sans-serif' },
  headerRow:    { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6, flexWrap: 'wrap', gap: 12 },
  title:        { margin: 0, color: '#1a1f36', fontSize: 24, fontWeight: 'bold' },
  hint:         { color: '#8792a2', fontSize: 12.5, marginBottom: 18, lineHeight: 1.6 },
  errorBanner:  { display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#ffebee', color: '#c62828', padding: 14, borderRadius: 6, marginBottom: 16 },
  closeErr:     { background: 'transparent', border: 'none', cursor: 'pointer', color: '#c62828', fontSize: 18 },
  successBanner:{ background: '#e8f5e9', color: '#2e7d32', padding: 12, borderRadius: 8, marginBottom: 16, fontSize: 13 },

  form:         { display: 'flex', flexDirection: 'column', gap: 18, maxWidth: 560 },
  fieldGroup:   { display: 'flex', flexDirection: 'column', gap: 6 },
  fieldLabel:   { fontSize: 13, fontWeight: 700, color: '#4f566b' },
  input:        { padding: '10px 12px', borderRadius: 10, border: '1.5px solid #e3e8ee', fontSize: 14, boxSizing: 'border-box', direction: 'rtl' },
  textarea:     { padding: '10px 12px', borderRadius: 10, border: '1.5px solid #e3e8ee', fontSize: 14, boxSizing: 'border-box', direction: 'rtl', minHeight: 90, resize: 'vertical', fontFamily: 'inherit' },

  targetToggle: { display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 },
  targetBtn:    { padding: '8px 14px', background: '#f5f7fa', color: '#4f566b', border: '1.5px solid #e3e8ee', borderRadius: 10, cursor: 'pointer', fontSize: 13, fontWeight: 700 },
  targetBtnActive: { background: '#EEF2FF', color: '#1f6feb', borderColor: '#1f6feb' },
  targetSummary:{ color: '#8792a2', fontSize: 12.5, margin: 0 },

  pickerWrap:   { display: 'flex', flexDirection: 'column', gap: 8 },
  chipsRow:     { display: 'flex', flexWrap: 'wrap', gap: 6 },
  chip:         { display: 'inline-flex', alignItems: 'center', gap: 6, background: '#EEF2FF', color: '#1f6feb', borderRadius: 999, padding: '4px 6px 4px 10px', fontSize: 12.5, fontWeight: 600 },
  chipRemove:   { background: 'transparent', border: 'none', cursor: 'pointer', color: '#1f6feb', fontSize: 12, padding: 2 },
  searchInput:  { padding: '8px 12px', borderRadius: 8, border: '1.5px solid #e3e8ee', fontSize: 13, boxSizing: 'border-box', direction: 'rtl' },
  userList:     { maxHeight: 220, overflowY: 'auto', border: '1px solid #e3e8ee', borderRadius: 10, padding: 6, display: 'flex', flexDirection: 'column', gap: 2 },
  userRow:      { display: 'flex', alignItems: 'center', gap: 8, padding: '7px 8px', borderRadius: 8, fontSize: 13, color: '#1a1f36', cursor: 'pointer' },
  userUsername: { color: '#8792a2', fontSize: 11.5 },
  emptyText:    { color: '#8792a2', fontSize: 12.5, padding: 10, textAlign: 'center' },

  sendBtn:      { alignSelf: 'flex-start', padding: '11px 22px', background: '#1f6feb', color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontSize: 14, fontWeight: 700 },
  sendBtnDisabled: { opacity: 0.5, cursor: 'not-allowed' },
};

export default NotificationsTab;
